import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpenIcon,
  UserIcon,
  Cog6ToothIcon,
  SunIcon,
  MoonIcon,
  LanguageIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  QuestionMarkCircleIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';

const Header = () => {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const getNavButtonStyles = (index, isLogout = false) => {
    if (isLogout) {
      return "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-md";
    }

    const styles = [
      "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800",
      "bg-gradient-to-r from-pink-600 to-rose-700 hover:from-pink-700 hover:to-rose-800",
      "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800",
      "bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800",
      "bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800",
      "bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800"
    ];

    return styles[index] || styles[0] + " shadow-md";
  };

  if (!user) {
    return null;
  }

  const navLinks = [
    { to: '/dashboard', label: language === 'ar' ? '\u0644\u0648\u062d\u0629 \u0627\u0644\u062a\u062d\u0643\u0645' : 'Dashboard' },
    { to: '/shorts', label: language === 'ar' ? '\u0641\u064a\u062f\u064a\u0648\u0647\u0627 \u0642\u0635\u064a\u0631\u0629' : 'Shorts' },

    { to: '/references', label: language === 'ar' ? '\u0627\u0644\u0645\u0631\u0627\u062c\u0639 \u0627\u0644\u0623\u0643\u0627\u062f\u064a\u0645\u064a\u0629' : 'Academic References' },
    // { to: '/quiz', label: language === 'ar' ? 'الاختبارات' : 'Quizzes' }, // Hidden - uncomment to show
    ...(user.role === 'admin' ? [{ to: '/admin', label: t('admin') }] : []),
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-slate-800 to-blue-800 shadow-lg backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center"
          >
            <Link to="/dashboard" className="flex items-center space-x-3 group">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="bg-gradient-to-r from-sky-600 to-blue-700 p-2.5 rounded-xl shadow-lg shadow-blue-500/20 group-hover:shadow-blue-500/40 transition-all duration-300"
              >
                <BookOpenIcon className="h-6 w-6 text-white" />
              </motion.div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-white drop-shadow-md tracking-tight group-hover:text-blue-100 transition-colors">
                  ETM Platform
                </h1>
                <p className="text-[11px] text-blue-200 font-medium tracking-wide uppercase opacity-80 group-hover:opacity-100 transition-opacity">
                  Medical Learning Platform
                </p>
              </div>
            </Link>
          </motion.div>

          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navLinks.map((link) => (
              <motion.div
                key={link.to}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to={link.to}
                  className="relative px-3 py-2 rounded-lg text-sm font-medium text-blue-100 hover:text-white hover:bg-white/10 transition-all duration-200 whitespace-nowrap flex items-center gap-2"
                >
                  {link.label}
                </Link>
              </motion.div>
            ))}
          </nav>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={toggleTheme}
              className="p-2 text-blue-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 rounded-md"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? (
                <MoonIcon className="h-5 w-5" />
              ) : (
                <SunIcon className="h-5 w-5" />
              )}
            </motion.button>

            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="relative"
            >
              <button
                onClick={toggleLanguage}
                className="flex items-center space-x-2 p-2 text-blue-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 rounded-md transition-all duration-200"
                title={language === 'en' ? 'Switch to Arabic' : 'Switch to English'}
                aria-label={language === 'en' ? 'Switch to Arabic' : 'Switch to English'}
              >
                <div className="relative">
                  <LanguageIcon className="h-5 w-5" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-[10px] font-bold">
                      {language === 'ar' ? 'AR' : 'EN'}
                    </span>
                  </div>
                </div>
                <span className="hidden sm:inline text-sm font-medium">
                  {language === 'ar' ? 'English' : '\u0627\u0644\u0639\u0631\u0628\u064a\u0629'}
                </span>
              </button>
            </motion.div>

            <div className="relative flex items-center space-x-2">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-white drop-shadow">
                  {user.fullName}
                </p>
                <p className="text-xs text-blue-200">
                  {user.studentId}
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleLogout}
                className="p-2 text-blue-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 rounded-md"
                title={t('logout')}
                aria-label={t('logout')}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </motion.button>
            </div>

            <motion.button
              whileHover={{ scale: 1.12 }}
              whileTap={{ scale: 0.95 }}
              className="md:hidden p-3 rounded-full border-2 border-blue-400 shadow-lg bg-gradient-to-r from-blue-600 via-purple-600 to-blue-400 text-white hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-4 focus:ring-blue-500 transition-all duration-200"
              style={{ boxShadow: '0 0 0 4px rgba(59,130,246,0.2)' }}
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-8 w-8" />
              ) : (
                <Bars3Icon className="h-8 w-8" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Menu - Rendered via Portal for proper z-index */}
      {isMenuOpen && ReactDOM.createPortal(
        <div className="md:hidden fixed inset-0" style={{ zIndex: 999999 }}>
          {/* Dark backdrop */}
          <div
            className="absolute inset-0 bg-black/80"
            onClick={toggleMenu}
          />

          {/* Sidebar panel - slides from left */}
          <div
            className="absolute top-0 left-0 h-full w-80 max-w-[85vw] shadow-2xl overflow-y-auto"
            style={{ backgroundColor: '#0f172a' }}
          >
            <div className="flex flex-col h-full p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-2">
                  <div className="bg-gradient-to-r from-sky-600 to-blue-700 p-2 rounded-lg shadow-md">
                    <BookOpenIcon className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    {language === 'ar' ? 'القائمة' : 'Menu'}
                  </h2>
                </div>
                <button
                  onClick={toggleMenu}
                  className="text-white p-2 rounded-md hover:bg-blue-700/50"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* User info */}
              <div className="mb-6 p-4 bg-white/10 rounded-xl border border-white/20">
                <div className="flex items-center space-x-4">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-full p-3">
                    <UserIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-base font-semibold text-white">{user.fullName}</p>
                    <p className="text-sm text-blue-200">{user.studentId}</p>
                  </div>
                </div>
              </div>

              {/* Navigation Links */}
              <nav className="flex flex-col space-y-3 flex-1">
                {navLinks.map((link, index) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={"flex items-center space-x-3 px-4 py-3 text-lg font-medium text-white rounded-lg " + getNavButtonStyles(index)}
                    onClick={toggleMenu}
                  >
                    <span className="w-6 h-6 flex items-center justify-center">
                      {index === 0 && <BookOpenIcon className="h-5 w-5" />}
                      {index === 1 && <PlayCircleIcon className="h-5 w-5" />}
                      {index === 2 && <BookOpenIcon className="h-5 w-5" />}
                      {index === 3 && <QuestionMarkCircleIcon className="h-5 w-5" />}
                      {index === 4 && <QuestionMarkCircleIcon className="h-5 w-5" />}
                      {index === 5 && user.role === 'admin' && <Cog6ToothIcon className="h-5 w-5" />}
                    </span>
                    <span>{link.label}</span>
                  </Link>
                ))}

                {/* Logout */}
                <button
                  className={"flex items-center space-x-3 px-4 py-3 text-lg font-medium text-white rounded-lg text-left " + getNavButtonStyles(0, true)}
                  onClick={handleLogout}
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  <span>{t('logout')}</span>
                </button>
              </nav>

              {/* Bottom section - Theme & Language */}
              <div className="mt-auto pt-4 flex flex-col space-y-3 border-t border-white/20">
                <button
                  onClick={toggleTheme}
                  className="flex items-center space-x-3 px-4 py-3 text-base font-medium text-white bg-gradient-to-r from-slate-600 to-slate-700 rounded-lg shadow-md"
                >
                  {theme === 'light' ? <MoonIcon className="h-5 w-5" /> : <SunIcon className="h-5 w-5" />}
                  <span>{theme === 'light' ? (language === 'ar' ? 'الوضع المظلم' : 'Dark Mode') : (language === 'ar' ? 'الوضع الفاتح' : 'Light Mode')}</span>
                </button>
                <button
                  onClick={toggleLanguage}
                  className="flex items-center space-x-3 px-4 py-3 text-base font-medium text-white bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg shadow-md"
                >
                  <LanguageIcon className="h-5 w-5" />
                  <span>{language === 'ar' ? 'English' : 'العربية'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default Header;
